import { useCallback, useEffect, useState } from 'react';
import { useNavigate, createSearchParams } from 'react-router';
import { useLocation } from 'react-router';
import { IConnectedPage } from '../../../../types/api/connectedPages/IConnectedPage';
import { isNullOrUndefined } from '../../../../lib/typeGuards';

function getPagesToDepth(connectedPages: IConnectedPage[], depth = 1): IConnectedPage[] {
  return depth <= 1
    ? []
    : [
        ...connectedPages,
        ...getPagesToDepth(
          connectedPages.flatMap(page => page.childPages || []),
          depth - 1,
        ),
      ];
}
function getChildren(page: IConnectedPage): IConnectedPage[] {
  return page.childPages ? [...page.childPages, ...page.childPages.flatMap(getChildren)] : [];
}

const getPageId = (page: IConnectedPage): string => {
  return page.pageId;
};

/**
 * Hook for the tree & org mode of the ConnectedPages*.
 *
 *
 * State Flow is as follows:
 *
 * Initialization:
 *  - no pages param -> &pages=&... (or non existent) -> use the default from JSON with its initaldepth
 *  - no pages are active -> &pages='none' -> init with empty array, meaning everything is closed
 *  - pages in adress bar -> &pages=nav1&pages=nav2 --> ini with this pages open
 *
 *  While opening/closing:
 * - opening or closing a tree/graph will navigate the url, by adding a part of the page url to the param
 *        -> /inhalte/Orgeinheit_1 will be represented as Orgeinheit_1
 * - the navigation will change the location.search param ( must not trigger SPA fetch route ! )
 * - this will trigger a useEffect change, where we collect the params from the address bar and keep state in sync.
 *
 * We opted to use this state flow, instead of changing the state on click and updating the url after this because.
 * - does not break browser back/forward button as component is updated with url
 * - no rendering of component when switching to pages where no params are given
 * - deeplinking is possible via SSR, when initalizing with search params
 */
export function useConnectedPagesExpandHandler(
  mode: 'tree' | 'org',
  connectedPages: IConnectedPage[] = [],
  initialVisibleDepth = 1,
) {
  const navigate = useNavigate();
  const location = useLocation();

  const getPagesFromUrl = useCallback(
    (params: string) => {
      const searchParams = createSearchParams(params);
      const pages = searchParams.get('pages');
      switch (pages) {
        case 'none':
          return [];
        case null:
          return getPagesToDepth(connectedPages, initialVisibleDepth)
            .filter(page => (page.childPages || []).length > 0) // ignore pages that have no children
            .map(getPageId);
        default:
          return searchParams.getAll('pages');
      }
    },
    [connectedPages, initialVisibleDepth],
  );

  /**
   * State including all expanded elements.
   * */
  const [expandedElements, setExpandedElements] = useState<string[]>(() => getPagesFromUrl(location.search));

  /**
   * Check if Page is currently open, e.g inside the expandedElements
   * */
  const isActive = (page: IConnectedPage): boolean => expandedElements.some(pageId => pageId === page.pageId);

  /**
   * Keep state in sync with url as long as we stay on the same mode
   */
  useEffect(() => {
    if (location.search.includes(`mode=${mode}`)) {
      const pages = getPagesFromUrl(location.search);
      setExpandedElements(pages);
    }
  }, [getPagesFromUrl, location.search, mode]);

  const updateExpandedElements = (page: IConnectedPage): string[] => {
    if (isNullOrUndefined(page)) {
      return expandedElements;
    }

    if (isActive(page)) {
      const toBeRemoved = [page, ...getChildren(page)].map(getPageId);
      return expandedElements.filter(item => !toBeRemoved.includes(item));
    }
    return [...expandedElements, page.pageId];
  };

  const onExpandedButtonClick = (page: IConnectedPage) => {
    const newElements = updateExpandedElements(page);
    const pages = newElements.length > 0 ? newElements : 'none';
    navigate({ search: createSearchParams({ mode, pages }).toString() }, { state: { refetch: 'ignoreQuery' } });
  };

  return { onExpandedButtonClick, isActive };
}
